import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowLeft } from "lucide-react";
import { TechChip } from "@/lib/tech";
import { TagChip } from "@/lib/tags";
import LinkedKeywords from "@/components/LinkedKeywords";
import SmartImage from "@/components/SmartImage";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import ArticleLangNote from "@/components/ArticleLangNote";
import { buildArticleSchema, clampText } from "@/lib/schema";
import { useRewrittenHtml } from "@/lib/useLinkPolicy";
import { useArticle, articleSeoProps, useArticleLangRoutes } from "@/lib/useArticle";

const InsightDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { item, err } = useArticle("insights", id);
  useArticleLangRoutes(item);

  const sanitizedBody = useMemo(
    () => DOMPurify.sanitize(item?.body || "", {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: ["target", "rel", "allow", "allowfullscreen", "frameborder", "loading"],
    }),
    [item?.body],
  );
  const renderedBody = useRewrittenHtml(sanitizedBody);

  if (err) return <div className="dl-container py-20"><Link to="/radar/insights" className="text-sm">← {t.nav.insights}</Link><h1 className="font-display text-3xl mt-4">{lang === "fr" ? "Contenu introuvable" : "Content not found"}</h1></div>;
  if (!item) return <div className="dl-container py-20 text-[#4B5563]" role="status">{lang === "fr" ? "Chargement du contenu…" : "Loading content…"}</div>;

  return (
    <article data-testid="insight-detail" className="dl-container py-10 max-w-4xl">
      <SeoHead
        lang={item.language || lang}
        {...articleSeoProps(item)}
        title={item.title}
        description={clampText(item.subtitle || item.body, 160) || "Dataleo insight on Supply Chain AI."}
        type="article"
        image={item.featured_image}
        publishedTime={item.published_at}
        modifiedTime={item.updated_at}
        jsonLd={buildArticleSchema({ baseUrl: SITE_URL, item, type: "Article", path: item.canonical_path })}
      />
      <Link to="/radar/insights" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#111827]">
        <ArrowLeft size={14} /> {t.nav.insights}
      </Link>
      <ArticleLangNote item={item} lang={lang} />
      <div className="mt-6 flex flex-wrap items-center gap-2 font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">
        <span>Dataleo Insight · {item.published_at}</span>
        {item.category && <span className="text-[#111827]">· {item.category}</span>}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#111827]">{item.title}</h1>
      {item.subtitle && <p className="mt-4 text-xl text-[#4B5563] leading-relaxed">{item.subtitle}</p>}
      {(item.technologies && item.technologies.length > 0) && (
        <div data-testid="insight-tech-list" className="flex flex-wrap gap-1.5 mt-5">
          {item.technologies.map((tech) => (
            <TechChip key={tech} name={tech} variant="dark" />
          ))}
        </div>
      )}
      {item.featured_image && (
        <div className="mt-8 border border-[#E5E7EB] overflow-hidden bg-[#F3F4F6]">
          <SmartImage src={item.featured_image} entity={item} type="insight" alt={item.title || "Dataleo insight cover"} className="w-full h-auto max-h-[420px]" />
        </div>
      )}
      <div
        className="tutorial-body mt-8 text-[16px] leading-[1.85] text-[#111827]"
        dangerouslySetInnerHTML={{ __html: renderedBody }}
      />
      <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-[#E5E7EB]">
        {(item.tags || []).map((tg) => (
          <TagChip key={tg} value={tg} />
        ))}
      </div>
      <LinkedKeywords keywords={item.linked_keywords} lang={lang} testid="insight-linked-keywords" />
      {item.url && (
        <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="insight-read-source"
            className="inline-flex items-center gap-2 bg-[#111827] hover:bg-[#00A878] text-white px-5 py-2.5 text-sm font-mono-dl uppercase tracking-wider transition-colors"
          >
            {lang === "fr" ? "Lire la source" : "Read the source"}
            {item.source && <span className="text-[#9ca3af] normal-case font-sans">- {item.source}</span>}
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      )}
    </article>
  );
};

export default InsightDetail;
